import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button, EditIcon, ResetIcon } from '@blade/ui-kit'
import { PairExploreItem } from '@root/features/explore/ui/pair-explore-item'

import type { PairModel } from '@root/entities/pair'
import type { ChildrenProps } from '@root/shared/react'

type Props = {
  imagePreview: string | undefined
  pair: PairModel | undefined
  onUploadClick: () => void
  onRevertClick?: () => void
} & ChildrenProps

export const PremiumImageDrawerPreview: React.FC<Props> = ({
  pair,
  imagePreview,
  onUploadClick,
  children,
  onRevertClick,
}) => {
  const { t } = useTranslation()

  return (
    <Root>
      <SubTitle>
        {t('premium.image-upload.size', { size: '512x512' })}{' '}
      </SubTitle>
      <Placeholder $imagePreview={imagePreview}>
        <Shadow>
          <ButtonOverlay
            type="button"
            iconLeft={<EditIcon />}
            size="xs"
            onClick={onUploadClick}
          />
          {onRevertClick ? (
            <ButtonOverlay
              type="button"
              iconLeft={<ResetIcon />}
              size="xs"
              onClick={onRevertClick}
            />
          ) : undefined}
        </Shadow>
      </Placeholder>
      {children}
      {pair ? (
        <>
          <Caption>{t('premium.update-logo.preview')}</Caption>
          <PairExploreItem
            pair={pair}
            statsPeriod="1D"
            priceChartInterval="minutes"
            iconSrcOverride={imagePreview}
          />
        </>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  padding: 0 16px;
`

const Placeholder = styled.div<{ $imagePreview: string | undefined }>`
  display: grid;

  width: 256px;
  height: 256px;
  flex-shrink: 0;

  justify-self: center;
  overflow: hidden;
  border-radius: 20px;

  background: ${($props) =>
    $props.$imagePreview ? `url(${$props.$imagePreview})` : '#0f1620'};

  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
`

const SubTitle = styled.p`
  color: var(--text-12);

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  margin: 10px 0 32px;
`

const Shadow = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 50%;

  box-shadow: 0 0 0 100px var(--appearanceA-3);
`

const Caption = styled.p`
  color: var(--text-12);

  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-transform: uppercase;
  margin: 16px 0;
`

const ButtonOverlay = styled(Button)`
  color: var(--text-12);

  border: 1px solid var(--background-5);

  border-radius: 64px;
  backdrop-filter: blur(30px);
  background: var(--background-3);

  &:hover {
    background: var(--background-3);
  }
`
